import type { LayerInfo } from "@esWebAPI/es/model/Layer";

type IThematicStyler = {
    getStyle: (styler, style, feature, layerViewInfo: LayerInfo) => any,
    getHideFeaturesCriteria : (thematic, attributeType) => any
};

export default class ThematicStyler {
    private thematicStyler: Map<string, IThematicStyler>;

    constructor() {
        const noneThematicStyler = new NoneThematicStyler();

        this.thematicStyler = new Map();
        this.thematicStyler.set("DistinctColorThematic", new DistinctColorThematicStyler());
        this.thematicStyler.set("DistinctSymbolThematic", new DistinctSymbolThematicStyler());
        this.thematicStyler.set("RangeColorThematic", new RangeColorThematicStyler());
        this.thematicStyler.set("RangeSizeThematic", new RangeSizeThematicStyler());
        this.thematicStyler.set("RangeSymbolThematic", new RangeSymbolThematicStyler());
        this.thematicStyler.set("NoneColorThematic", noneThematicStyler);
        this.thematicStyler.set("NoneSizeThematic", noneThematicStyler);
        this.thematicStyler.set("NoneSymbolThematic", noneThematicStyler);
    }

    private getThematicStyler(thematic): IThematicStyler {
        const thematicStyler = this.thematicStyler.get(thematic.$type);
        if (thematicStyler == null) {
            throw new Error("Invalid thematic: " + thematic.$type);
        }

        return thematicStyler;
    }

    getStyle(styler, style, feature, layerViewInfo: LayerInfo) {
        var thematic = styler.thematic;
        var attr = layerViewInfo.attributes.get('name', thematic.attribute);
        if(!attr){
            return style; // bullet proofing - just in case somehow the thematic is wrong, we return the default
        }
        
        return this.getThematicStyler(thematic).getStyle(styler, style, feature, layerViewInfo);
    }
    
    getPaddingPx(styler): number[] {
        var thematic = styler.thematic;
        var style;
        if (thematic.$type === "DistinctColorThematic" || thematic.$type === "RangeColorThematic") {
            style = styler.defaultStyle;
        } else { // "RangeSizeThematic"
            style = $.extend(true, {}, styler.defaultStyle, {size: thematic.maxSize});
        }
        
        return es.util.style.types[style.$type].getPaddingPx(style);
    }
    
    hasDirect(styler): boolean {
        return es.util.style.types[styler.defaultStyle.$type].hasDirect(styler.defaultStyle);
    }
    
    hasOverlays(styler): boolean {
        return es.util.style.types[styler.defaultStyle.$type].hasOverlays(styler.defaultStyle);
    }
    
    getHideFeaturesCriteria(styler) {
        return this.getThematicStyler(styler.thematic).getHideFeaturesCriteria(styler.thematic, styler.attributeType);
    }
}


abstract class AThematicStyler implements IThematicStyler {
    abstract getStyle(styler, style, feature, layerViewInfo: LayerInfo);

    abstract getHideFeaturesCriteria(thematic, attributeType: string);

    /**
     * 
     * @param strVal 
     * @param attrType 
     * 
     * @returns 
     */
    protected getRangeVal(strVal: string, attrType: string) {
        if (!strVal) {
            return (attrType === "STRING" ? '' : null);
        }
        
        if (attrType === "STRING") {
            return strVal;
        }
        
        if (attrType === "TIMESTAMP") {
            return new Date(Number(strVal));
        }
        
        if ((attrType === "INTEGER") || (attrType === "DECIMAL")) {
            return parseFloat((strVal + '').replaceAll(",",""));
        }
        
        if (attrType == "BOOLEAN") {
            return (strVal.toLowerCase() == "true");
        }
        
        if (es.isDeveloper) {
            es.log.error('Invalid attrType: ' + attrType);
            debugger;
        }
        
        return strVal; // should never happen!
    }

    /**
     * 
     * @param baseStyle 
     * @param newColor 
     * 
     * @returns 
     */
    protected getSameTransparencyColor(baseStyle, newColor) {
        let baseColor = this.getBaseColor(baseStyle);
        let transparency = es.util.color.getTransparency(baseColor);
        return es.util.color.withTransparency(newColor, transparency);
    }
    
    /**
     * 
     * @param baseStyle 
     * 
     * @returns 
     */
    protected getBaseColor(baseStyle): number {
        switch(baseStyle.$type) {
            case "SymbolStyle": return baseStyle.color;
            case "SymbolAndPolygonStyle": return this.getBaseColor(baseStyle.symbolStyle);
            case "PolygonStyle": return (baseStyle.fillColor ? baseStyle.fillColor : 0x7F000000); // if no fill color make 50% Transparent
            case "LineStyle": return baseStyle.line.color;
            default: return 0; // TODO ??!!!
        }
    }
}

abstract class ADistinctThematicStyler extends AThematicStyler {
    /**
     * get distinct thematic range entry that matches value. null if none found.
     * 
     * @param thematic 
     * @param value 
     * @param layerViewInfo 
     * 
     * @returns 
     */
    protected getDistinctThematicRangeEntry(thematic, value, layerViewInfo: LayerInfo) {
        var attr = layerViewInfo.attributes.get('name', thematic.attribute);
        var attrType = attr.attrType;
        var ranges = thematic.ranges;
        for (var r = 0; r < ranges.length; r++) {
            var range = ranges[r];
            var rangeVal = this.getRangeVal(range.val, attrType);
            
            if((rangeVal === value) || (!range.val && value === null)) {
                return range;
            }
        }
        
        return null;
    }

    /**
     * 
     * @param attribute 
     * @param attributeType 
     * @param ranges 
     * 
     * @returns 
     */
    protected getDistinctThematicHideFeaturesCriteria(attribute, attributeType: string, ranges: any[]) {
        var criterias = ranges.filter(function(r) {
            return r.hideFeatures;
        }).map((r) => {
            return {
                '$type': 'NotEqual',
                'attr': attribute,
                'value': this.getRangeVal(r.val, attributeType)
            };
        });

        return es.util.criteria.and.apply(es.util.criteria, criterias);
    }
}

class DistinctColorThematicStyler extends ADistinctThematicStyler {
    /**
     * 
     * @param styler 
     * @param style 
     * @param feature 
     * @param layerViewInfo 
     * 
     * @returns 
     */
    getStyle(styler, style, feature, layerViewInfo: LayerInfo) {
        var thematic = styler.thematic;
        var value = (thematic.attribute === "ES_ID") ? feature.id : feature.attrs[thematic.attribute];
        
        var range = this.getDistinctThematicRangeEntry(thematic, value, layerViewInfo);
        if (range) {
            style = es.api.style.withColor(style, this.getSameTransparencyColor(style, range.color));
        }
        
        return style;
    }
    
    /**
     * 
     * @param thematic 
     * @param attributeType 
     * 
     * @returns 
     */
    getHideFeaturesCriteria(thematic, attributeType: string) {
        return this.getDistinctThematicHideFeaturesCriteria(thematic.attribute, attributeType, thematic.ranges);
    }
}

class DistinctSymbolThematicStyler extends ADistinctThematicStyler {
    /**
     * 
     * @param styler 
     * @param style 
     * @param feature 
     * @param layerViewInfo 
     * 
     * @returns 
     */
    getStyle(styler, style, feature, layerViewInfo: LayerInfo) {
        var thematic = styler.thematic;
        var value = (thematic.attribute === "ES_ID") ? feature.id : feature.attrs[thematic.attribute];
        
        var range = this.getDistinctThematicRangeEntry(thematic, value, layerViewInfo);
        if (range) {
            style = es.api.style.withSymbol(style, range.symbolTypeId);
        }
        
        return style;
    }
    
    /**
     * 
     * @param thematic 
     * @param attributeType 
     * 
     * @returns 
     */
    getHideFeaturesCriteria(thematic, attributeType: string) {
        return this.getDistinctThematicHideFeaturesCriteria(thematic.attribute, attributeType, thematic.ranges);
    }
}

abstract class ARangeThematicStyler extends AThematicStyler {
    /**
     * get range thematic range entry that matches value. null if none found.
     * 
     * @param thematic 
     * @param value 
     * @param layerViewInfo 
     * 
     * @returns 
     */
    protected getRangeThematicRangeEntry(thematic, value, layerViewInfo: LayerInfo) {
        var attr = layerViewInfo.attributes.get('name', thematic.attribute);
        var attrType = attr.attrType;
        
        if (value != null) {
            var ranges = thematic.ranges;
            for(var r = 0; r < ranges.length; r++) {
                var range = ranges[r];
                var rangeVal = this.getRangeVal(range.val, attrType);
                
                if (value <= rangeVal || r==ranges.length-1) {
                    return range;
                }
            }
        }
        
        return null;
    }

    /**
     * 
     * @param attribute 
     * @param attributeType 
     * @param ranges 
     * 
     * @returns 
     */
    protected getRangeThematicHideFeaturesCriteria(attribute, attributeType: string, ranges: any[]) {
        const criterias = [];
        for (let i = 0; i < ranges.length; i++) {
            const r = ranges[i];

            if (r.hideFeatures) {
                if (i == ranges.length - 1) {
                    const rangeVal = this.getRangeVal(r.val, attributeType);
                    criterias.push({
                        '$type': (attributeType == 'TIMESTAMP' ? 'DateGreater' : 'Greater'),
                        'attr': attribute,
                        'value': (attributeType == 'TIMESTAMP' ? rangeVal.getTime() : rangeVal)
                    });
                } else {
                    var firstCriteria = null;
                    if (i > 0) {
                        const rangeVal = this.getRangeVal(ranges[i-1].val, attributeType);
                        firstCriteria = {
                            '$type': (attributeType == 'TIMESTAMP' ? 'DateGreater' : 'Greater'),
                            'attr': attribute,
                            'value': (attributeType == 'TIMESTAMP' ? rangeVal.getTime() : rangeVal)
                        };
                    }

                    for (i++; i < ranges.length && ranges[i].hideFeatures; i++)
                        ;

                    if (i == ranges.length) {
                        if (firstCriteria == null) {
                            // all are hide features, so only show null values (they're not thematized)
                            return {
                                '$type': 'Equal',
                                'attr': attribute,
                                'value': null
                            };
                        }
                        
                        criterias.push(firstCriteria);
                    } else {
                        const rangeVal = this.getRangeVal(ranges[i - 1].val, attributeType);
                        criterias.push(es.util.criteria.and(firstCriteria, {
                            '$type': (attributeType == 'TIMESTAMP' ? 'DateLessEqual' : 'LessEqual'),
                            'attr': attribute,
                            'value': (attributeType == 'TIMESTAMP' ? rangeVal.getTime() : rangeVal)
                        }));
                    }
                }
            }
        }
        
        let c = es.util.criteria.or.apply(es.util.criteria, criterias);
        if (c != null) {
            // null values are not hidden (they're not even thematized)
            c = es.util.criteria.or({
                '$type': 'Not',
                'criteria': c
            }, {
                '$type': 'Equal',
                'attr': attribute,
                'value': null
            });
        }

        return c;
    }
}

class RangeColorThematicStyler extends ARangeThematicStyler {
    /**
     * 
     * @param styler 
     * @param style 
     * @param feature 
     * @param layerViewInfo 
     * 
     * @returns 
     */
    getStyle(styler, style, feature, layerViewInfo: LayerInfo) {
        var thematic = styler.thematic;
        var value = (thematic.attribute === "ES_ID") ? feature.id : feature.attrs[thematic.attribute];
        
        var range = this.getRangeThematicRangeEntry(thematic, value, layerViewInfo);
        if (range) {
            style = es.api.style.withColor(style, this.getSameTransparencyColor(style, range.color));
        }
        
        return style;
    }
    
    /**
     * 
     * @param thematic 
     * @param attributeType 
     * 
     * @returns 
     */
    getHideFeaturesCriteria(thematic, attributeType: string) {
        return this.getRangeThematicHideFeaturesCriteria(thematic.attribute, attributeType, thematic.ranges);
    }
}

class RangeSizeThematicStyler extends ARangeThematicStyler {
    /**
     * 
     * @param styler 
     * @param style 
     * @param feature 
     * @param layerViewInfo 
     * 
     * @returns 
     */
    getStyle(styler, style, feature, layerViewInfo: LayerInfo) {
        var thematic = styler.thematic;
        var value = (thematic.attribute === "ES_ID") ? feature.id : feature.attrs[thematic.attribute];
        
        if (value == null || value <= thematic.minValue) {
            value = thematic.minValue;
        } else if (value > thematic.maxValue) {
            value = thematic.maxValue;
        }
        
        var percent = 1;
        //Don't divide by zero
        if(thematic.maxValue != thematic.minValue) {
            percent = (value - thematic.minValue) / (thematic.maxValue - thematic.minValue);
        }
        var size = Math.round(((thematic.maxSize - thematic.minSize) * percent) + thematic.minSize);

        var styleWithSize = es.util.style.types[style.$type].withSymbolSize(style, size);
        return styleWithSize;
    }

    /**
     * 
     * @param thematic 
     * @param attributeType 
     * 
     * @returns 
     */
    getHideFeaturesCriteria(thematic, attributeType: string) {
        return null;
    }
}

class RangeSymbolThematicStyler extends ARangeThematicStyler {
    getStyle(styler, style, feature, layerViewInfo: LayerInfo) {
        var thematic = styler.thematic;
        var value = (thematic.attribute === "ES_ID") ? feature.id : feature.attrs[thematic.attribute];
        
        var range = this.getRangeThematicRangeEntry(thematic, value, layerViewInfo);
        if (range) {
            style = es.api.style.withSymbol(style, range.symbolTypeId);
        }
        
        return style;
    }
    
    getHideFeaturesCriteria(thematic, attributeType: string) {
        return this.getRangeThematicHideFeaturesCriteria(thematic.attribute, attributeType, thematic.ranges);
    }
}

class NoneThematicStyler extends AThematicStyler {
    getStyle(styler, style, feature, layerViewInfo: LayerInfo) {
        return style;
    }
    
    getHideFeaturesCriteria(thematic, attributeType: string) {
        return null;
    }
}
